import * as React from 'react'
import styled, { css } from 'styled-components'
import type { FlattenSimpleInterpolation } from 'styled-components'

import * as behaviours from '../behaviours'
import * as bp from '../constants/breakpoints'
import * as colors from '../constants/colors'
import * as S from '../styles'

type WysiwygContentProps = {
  className?: string
  /** @default 'eager' */
  loading?: 'eager' | 'lazy'
  nativeLazyLoad?: boolean
  textSize?: 'small' | 'medium' | 'large'
} & React.HTMLAttributes<HTMLDivElement>

const WysiwygContentComponent = React.forwardRef<HTMLDivElement, WysiwygContentProps>(function WysiwygContentComponent(
  { loading = 'eager', nativeLazyLoad, dangerouslySetInnerHTML, className, ...rest },
  ref,
): React.ReactElement<WysiwygContentProps> {
  if (loading === 'lazy') {
    return (
      <behaviours.LazyWysiwyg nativeLazyLoad={nativeLazyLoad}>
        <div ref={ref} className={className} dangerouslySetInnerHTML={dangerouslySetInnerHTML} {...rest} />
      </behaviours.LazyWysiwyg>
    )
  } else {
    return <div ref={ref} className={className} dangerouslySetInnerHTML={dangerouslySetInnerHTML} {...rest} />
  }
})

function getTextSize(size: WysiwygContentProps['textSize']): FlattenSimpleInterpolation {
  if (size === 'small') {
    return css`
      ${S.copy._smallStyle}
    `
  } else if (size === 'large') {
    return css`
      ${S.copy._largeStyle}
    `
  } else {
    return css`
      ${S.copy._mediumStyle}
    `
  }
}

export const WysiwygContent = styled(WysiwygContentComponent)<WysiwygContentProps>`
  word-wrap: break-word;

  a {
    ${S.link._baseStyle}
    ${S.link._primaryStyle}
    ${S.link._primaryTintedColor}
    font-weight: 300;
    overflow-wrap: break-word;
  }

  blockquote {
    ${S.heading._largeStyle}
    display: inline-flex;
    line-height: 38px;
    margin-left: 0;

    &::before {
      border-left: 2px ${colors.GREY_SHADE_5} solid;
      content: '';
      padding-right: 26px;
    }
  }

  h1,
  h2 {
    ${S.heading._xxxSmallStyle}
  }

  h3,
  h4,
  h5,
  h6 {
    ${S.heading._xxxxSmallStyle}
  }

  @media screen and ${bp.TABLET} {
    h1,
    h2 {
      ${S.heading._mediumStyle}
    }

    h3 {
      ${S.heading._smallStyle}
    }

    h4 {
      ${S.heading._xSmallStyle}
    }

    h5 {
      ${S.heading._xxSmallStyle}
    }

    h6 {
      ${S.heading._xxxSmallStyle}
    }
  }

  iframe,
  img {
    max-width: 100%;
  }

  /* Exclude Soundcloud widgets from height assignment. When we alter height, Soundcloud fails to compute
  ** the correct height on mobile and hides the track list.
   */

  iframe:not([src*='soundcloud.com', src*='reetags.com']),
  img {
    /* NOTE: Force auto-height to avoid user-defined custom values breaking aspect ratio on small screens. */
    @media not screen and ${bp.TABLET} {
      height: auto !important;
    }
  }

  .image {
    margin: 0.9em auto;

    img {
      margin: 0 auto;
    }
  }

  img[align='left'],
  img[style*='float:left'],
  img[style*='float: left'] {
    margin-right: 40px;
  }

  .image-style-align-left {
    margin: 0.9em 40px 0.9em auto;
    float: left;
  }

  img[align='right'],
  img[style*='float:right'],
  img[style*='float: right'] {
    margin-left: 40px;
  }

  .image-style-align-right {
    margin: 0.9em auto 0.9em 40px;
    float: right;
  }

  .image-style-block-align-left {
    margin-left: 0;
    margin-right: auto;
  }

  .image-style-block-align-right {
    margin-right: 0;
    margin-left: auto;
  }

  figure {
    margin-left: 0;
    margin-right: 0;

    img {
      display: block;
    }
  }

  .image-column figure {
    margin-top: 0;
    margin-bottom: 0;
    display: flex;
    justify-content: center;
    align-items: baseline;
  }

  .media .media-wrapper {
    text-align: center;

    .embed-sizer {
      margin: 0 auto;

      iframe {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
      }
    }
  }

  li {
    ${({ textSize }) => getTextSize(textSize)}
    & > p {
      display: inline;
    }
  }

  ol,
  p,
  strong,
  ul:not(.ck-list) {
    ${({ textSize }) => getTextSize(textSize)}

    margin: 1em 0;
  }

  ol,
  strong,
  ul:not(.ck-list) {
    color: inherit;
  }

  ol:not(.ck-list),
  ul:not(.ck-list) {
    padding-left: 20px;
  }

  ol:not(.ck-list) {
    counter-reset: item;

    & > li {
      counter-increment: item;
      list-style-type: none;
      margin: 0;
      padding: 0 0 0 2em;
      text-indent: -2em;

      &::before {
        content: counter(item) '.';
        display: inline-block;
        font-weight: 500;
        padding-right: 0.5em;
        text-align: right;
        width: 1em;
      }
    }
  }

  && {
    strong {
      font-weight: bold;
    }
  }

  ul:not(.ck-list) {
    list-style: none;

    & > li::before {
      content: '•';
      padding-right: 0.5em;
    }
  }

  s + u::before {
    margin-right: 2px;
  }
`
WysiwygContent.displayName = 'WysiwygContent'
